import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { Nav, NavItem, NavLink } from 'reactstrap';
import styled from 'styled-components';
import { AtomCategory } from '../../atoms/utils/model/Model';
import { sortOwners } from '../../components_v2/dropdown/DropdownOwners';
import { UserLabel } from '../../components_v2/dropdown/style/Style';
import ListPicker from '../../components_v2/listPicker/listPicker';
import ToolbarFilter, { ToolbarElement } from '../../components_v2/toolbarFilter/ToolbarFilter';
import storeLang from '../../helpers/storeLang';
import { Checkbox } from '../../components_v2/dropdown/style/Style';
import { Dot } from '../../styles/global/css/Dot';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { ButtonToggle } from '../../styles/global/css/Open';
import { PrimaryColor, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Owner } from '../orders/model/Model';
import { FlexDiv } from '../products/style';
import { EventsTypes, EventsTypesUtils } from './Calendar';
import { initialCalendarFilters } from './CalendarView';
import { colorTypeMapping, RepresentationMode } from './model/Model';
import { CalendarModalSubContainer, CalendarModalTitle } from './style/style';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

const REPRESENTATION_MODES: Array<[RepresentationMode, string]> = [
	['per_types', 'type'],
	['per_users', 'User']
];

// A plain styled checkbox row: the bootstrap-css CheckboxGroupFilter used by
// the elements section depends on page-level classes and broke in this panel
function FilterCheckRow(props: { label: React.ReactNode, checked: boolean, onClick: () => void }) {
	return <FlexDiv gap='10px' align='center' cursor='pointer' onClick={props.onClick}>
		<Checkbox isActive={props.checked} />
		<DefaultTextDiv style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{props.label}</DefaultTextDiv>
	</FlexDiv>;
}

export default function ModalLeft(props: {
  handleFilter: (key: string, value?: any) => void,
  onRepresentationChange: (value: RepresentationMode) => void
  colors?: Array<{ owner: Owner, color: string }>
  panelView?: boolean
  includeDrafts?: boolean
  onIncludeDraftsChange?: (value: boolean) => void
  // form/chunk display filters: options come from the fetched window, the
  // hidden lists are what the user unchecked
  formFilterOptions?: { forms: { id: number, name: string }[], bounds: string[] }
  hiddenForms?: number[]
  hiddenBounds?: string[]
  onHiddenFormsChange?: (value: number[]) => void
  onHiddenBoundsChange?: (value: string[]) => void
  isModalOpen?: boolean
  setIsModalOpen?: (value: boolean) => void
}) {
	const translate = getTranslate(storeLang.getState().localize);
	const types = [
		{ label: translate('event.events'), value: EventsTypes.Event },
		{ label: translate('campagnes'), value: EventsTypes.Promotion },
		{ label: translate('forms'), value: EventsTypes.Form },
	];
	const [localIsOpen, setLocalIsOpen] = React.useState(false);
	const isOpen = props.isModalOpen ?? localIsOpen;
	const setIsOpen = props.setIsModalOpen ?? setLocalIsOpen;
	const [activeCollapse, setActiveCollapse] = React.useState<string[]>(['elements']);
	const localStorageEventFiltersStatus = localStorage.getItem('eventFiltersStatus');
	const [statusCheckBox, setStatusCheckBox] = React.useState<EventsTypes[]>(localStorageEventFiltersStatus ? JSON.parse(localStorageEventFiltersStatus) : initialCalendarFilters(false).types);
	const [selectedRepresentation, setSelectedRepresentation_] = React.useState<RepresentationMode>(REPRESENTATION_MODES[0][0]);
	const [activeTab, setActiveTab] = React.useState<number>(1);

	const setSelectedRepresentation = (value: RepresentationMode) => {
		setSelectedRepresentation_(value);
		props.onRepresentationChange(value);
	};

	function toggle(type: string) {
		const active = [...activeCollapse];
		if (active.includes(type)) {
			active.splice(active.indexOf(type), 1);
		} else {
			active.push(type);
		}
		setActiveCollapse(active);
	}

	function buttonToggle(title: string, key: string) {
		return (
			<ButtonToggle
				isOpen={activeCollapse.includes(key)}
				onClick={() => toggle(key)}
				title={title}
				key={key}
			/>
		);
	}

	function handleCheckedStatus(isChecked: boolean, value: EventsTypes | undefined) {
		let newValue = statusCheckBox;
		if (value !== undefined) {
			if (isChecked) {
				newValue.push(value);
			} else {
				const index = newValue.indexOf(value);
				if (index >= 0) { newValue.splice(index, 1); }
			}
		} else {
			if (isChecked) {
				newValue = types.map(e => e.value);
			} else {
				newValue = [];
			}
		}
		localStorage.setItem('eventFiltersStatus', JSON.stringify(newValue));
		setStatusCheckBox([...newValue]);
		props.handleFilter('types', newValue);
	}

	function handleReset() {
		setStatusCheckBox(initialCalendarFilters(false).types);
		props.handleFilter('reset');
	}

	const child = <>
		<Nav tabs>
			<NavItem>
				<NavLink
					className={activeTab === 1 ? 'active' : ''}
					onClick={() => setActiveTab(1)}
				>
					<Translate id="events" />
				</NavLink>
			</NavItem>
			<NavItem>
				<NavLink
					className={activeTab === 2 ? 'active' : ''}
					onClick={() => setActiveTab(2)}
				>
					<Translate id="Filters" />
				</NavLink>
			</NavItem>
		</Nav>
		{activeTab == 1 &&
			<FlexDiv flow='column' gap='50px' padding='20px 0 0 0' overflow='auto'>
				<div style={{ width: '100%' }}>
					<CalendarModalSubContainer>
						<CalendarModalTitle>
							<Translate id='event_representation' />
						</CalendarModalTitle>
					</CalendarModalSubContainer>
					{REPRESENTATION_MODES.map(([mode, translation], index) => <ListPicker
						key={`ModalLeftCalendarREPRESENTATION_MODES[${index}]`}
						selected={selectedRepresentation == mode}
						value={translate(translation).toString()}
						onSelect={() => setSelectedRepresentation(mode)}
					/>)}
				</div>
				<CalendarModalSubContainer>
					<CalendarModalTitle>
						<Translate id='event_legend' />
					</CalendarModalTitle>
					{selectedRepresentation == 'per_users' && props.colors?.sort((a, b) => sortOwners(a.owner, b.owner)).map((c, i) => <FlexDiv key={`modalLeftCalendarUser[${i}]`} gap='20px'>
						<Dot color={c.color} size='10px' />
						<UserLabel me={translate('me').toString()} user={c.owner}>{c.owner.name}</UserLabel>
					</FlexDiv>)}
					{selectedRepresentation == 'per_types' &&
						<>
							{statusCheckBox.includes(EventsTypes.Event) && colorTypeMapping.map((mapping, i) => <FlexDiv key={`modalLeftCalendarEventColor[${i}]`} gap='20px'>
								<Dot color={mapping.color} size='10px' />
								<DefaultTextDiv><Translate id={`event.${mapping.label}`} /></DefaultTextDiv>
							</FlexDiv>)
							}
							{statusCheckBox.filter(e => e !== EventsTypes.Event && e !== EventsTypes.Promotion).map(et => <>
								<FlexDiv gap='20px'>
									<Dot color={SidelyBlack} size='10px' style={{ outline: `${colorTypeMapping[et - 1].color} solid 4px` }} />
									<DefaultTextDiv><Translate id={EventsTypesUtils.toTranslateString(et)} /></DefaultTextDiv>
								</FlexDiv>
							</>)}
						</>
					}
					{statusCheckBox.includes(EventsTypes.Promotion) &&
						<FlexDiv gap='20px'>
							<Dot color='white' size='10px' style={{ outline: `${PrimaryColor} solid 2px` }} />
							<DefaultTextDiv><Translate id='campagne' /></DefaultTextDiv>
						</FlexDiv>
					}
				</CalendarModalSubContainer>
			</FlexDiv>
		}
		{activeTab == 2 &&
			<FlexDiv flow='column' gap='50px' padding='20px 0 0 0' overflow='auto'>
				<CalendarModalSubContainer>
					<FlexDiv justify='space-between' width='100%' margin='0 0 20px 0'>
						<CalendarModalTitle noMargin>
							<Translate id='Quick filters' />
						</CalendarModalTitle>
						<button
							type='button'
							className='btn btn-primary-outline'
							style={{
								padding: '5px 15px',
								borderRadius: 5
							}}
							onClick={() => {
								handleReset();
							}}
						>
							<Translate id='Reset All' />
						</button>
					</FlexDiv>
					{ props.panelView &&
						<ToolbarFilter
							vertical
							permission='ReadEvent'
							category={AtomCategory.CALENDAR}
							elements={[
								{
									kind: ToolbarElement.USER_FILTER,
									user: 'me'
								},
								ToolbarElement.EVENT_STATUS_FILTER,
								ToolbarElement.EVENT_TYPE_FILTER,
								ToolbarElement.TAG_FILTER
							]}
						/>}
					<div style={{ width: '100%' }}>
						{buttonToggle('global.elements', 'elements')}
						{/* same plain rows as the sections below: the animated Collapse
						    pins a measured height and the bootstrap checkbox component
						    depends on page css, and both kept breaking this panel */}
						{activeCollapse.includes('elements') && <FlexDiv flow='column' gap='8px' align='stretch' style={{ maxWidth: props.panelView ? undefined : '260px', padding: '2px 0 8px 0' }}>
							{types.map(type => <FilterCheckRow
								key={`calendarElementFilter[${type.value}]`}
								label={type.label as React.ReactNode}
								checked={statusCheckBox.includes(type.value)}
								onClick={() => handleCheckedStatus(!statusCheckBox.includes(type.value), type.value)}
							/>)}
						</FlexDiv>}
					</div>
					{/* never gated on fetched rows: when the window only held
					    drafts, unchecking the draft toggle used to unmount its own
					    checkbox and strand the user */}
					{statusCheckBox.includes(EventsTypes.Form) && <div style={{ width: '100%' }}>
						{buttonToggle('forms', 'form_filters')}
						{/* no animated Collapse here: it pins a measured height while
						    these lists resize with every fetch, and a stale height lets
						    the content paint over the next section */}
						{activeCollapse.includes('form_filters') && <FlexDiv flow='column' gap='8px' align='stretch' style={{ maxWidth: props.panelView ? undefined : '260px', maxHeight: '300px', overflowY: 'auto', padding: '2px 0 8px 0' }}>
							<FilterCheckRow
								label={translate('form_editor.calendar_display.drafts').toString()}
								checked={!!props.includeDrafts}
								onClick={() => props.onIncludeDraftsChange?.(!props.includeDrafts)}
							/>
							{(props.formFilterOptions?.forms.length ?? 0) > 0 && <FilterCheckRow
								label={translate('form_editor.calendar_display.all').toString()}
								checked={!props.formFilterOptions?.forms.some(f => props.hiddenForms?.includes(f.id))}
								onClick={() => {
									const allHidden = props.formFilterOptions?.forms.some(f => props.hiddenForms?.includes(f.id));
									props.onHiddenFormsChange?.(allHidden ? [] : props.formFilterOptions?.forms.map(f => f.id) ?? []);
								}}
							/>}
							{props.formFilterOptions?.forms.map(form => <FilterCheckRow
								key={`calendarFormFilter[${form.id}]`}
								label={form.name}
								checked={!props.hiddenForms?.includes(form.id)}
								onClick={() => {
									const hidden = props.hiddenForms ?? [];
									props.onHiddenFormsChange?.(hidden.includes(form.id) ? hidden.filter(id => id !== form.id) : [...hidden, form.id]);
								}}
							/>)}
						</FlexDiv>}
					</div>}
					{statusCheckBox.includes(EventsTypes.Form) && (props.formFilterOptions?.bounds.length ?? 0) > 1 && <div style={{ width: '100%' }}>
						{buttonToggle('form_editor.calendar_display.label', 'bound_filters')}
						{activeCollapse.includes('bound_filters') && <FlexDiv flow='column' gap='8px' align='stretch' style={{ maxWidth: props.panelView ? undefined : '260px', maxHeight: '300px', overflowY: 'auto', padding: '2px 0 8px 0' }}>
							{props.formFilterOptions?.bounds.map(bound => <FilterCheckRow
								key={`calendarBoundFilter[${bound}]`}
								label={bound === '' ? translate('form_editor.calendar_display.main').toString() : bound}
								checked={!props.hiddenBounds?.includes(bound)}
								onClick={() => {
									const hidden = props.hiddenBounds ?? [];
									props.onHiddenBoundsChange?.(hidden.includes(bound) ? hidden.filter(b => b !== bound) : [...hidden, bound]);
								}}
							/>)}
						</FlexDiv>}
					</div>}
				</CalendarModalSubContainer>
			</FlexDiv>
		}
	</>;

	if (props.panelView) {
		return <div style={{ position: 'absolute', bottom: 0, width: '100%', overflow: 'unset', height: 'fit-content' }} className='box-filter-modal'>
			<VerticalWrapperWrapper isOpen={isOpen}>
				<button
					type='button'
					className='btn btn-filter-modal'
					style={{ padding: '0.15rem 2rem', position: 'absolute', left: '50%', translate: '-50% -100%' }}
					onClick={() => setIsOpen(!isOpen)}
				>
					<Arrow isOpen={isOpen}/>
				</button>
				<VerticalWrapper>
					{ child }
				</VerticalWrapper>
			</VerticalWrapperWrapper>
		</div>;
	}

	return (
		<div className='filter-modal'
			style={{ top: TOOLBARS_HEIGHT, height: `calc(100vh - ${TOOLBARS_HEIGHT}px)`, overflow: 'visible', zIndex: 5, left: '0' }}>
			<div className={isOpen ? 'box-filter-modal' : 'd-none'} style={{ width: '275px' }}>
				{ child }
			</div>
		</div>
	);
}

const VERTICAL_HEIGHT = '75vh';

const VerticalWrapperWrapper = styled.div<{ isOpen: boolean }>`
	width: 100%;
	transition: 0.5s;
	position: absolute;
	translate: 0 ${({ isOpen }) => isOpen ? '-100%' : '0'};
	z-index: 4;
`;

const VerticalWrapper = styled.div`
	height: ${VERTICAL_HEIGHT};
	background-color: ${SurfaceColor};
	display: grid;
	grid-template-rows: auto 1fr;
`;

const Arrow = styled.div<{ isOpen: boolean }>`
	width: 0;
	height: 0;
	border-left: 6px solid transparent;
	border-right: 6px solid transparent;
	border-${({ isOpen }) => isOpen ? 'top' : 'bottom'}: 6px solid ${SurfaceColor};
`;
